import { motion } from 'framer-motion';
import { BarChart3 } from 'lucide-react';
import { useState } from 'react';

interface ChurnByTenureChartProps {
  data: Record<string, number>;
  title?: string;
  isLoading?: boolean;
}

const ChurnByTenureChart = ({
  data,
  title = 'Essentials Churn by Tenure',
  isLoading = false,
}: ChurnByTenureChartProps) => {
  const [hoveredBucket, setHoveredBucket] = useState<string | null>(null);

  // Calculate max value for scaling
  const maxValue = Math.max(...Object.values(data), 1);

  // Calculate churn rate percentages
  const totalChurned = Object.values(data).reduce((sum, val) => sum + val, 0);

  const buckets = [
    '1 month',
    '3 month',
    '6 month',
    '9 month',
    '12 month',
    '15 month',
    '18 month',
    '21 month',
    '24 month',
    '25+ month',
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading chart data...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="relative bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl border border-gray-200 dark:border-gray-700 overflow-visible"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-xl">
          <BarChart3 className="h-6 w-6 text-red-600 dark:text-red-400" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Total Churned: {totalChurned} subscribers
          </p>
        </div>
      </div>

      {/* Chart */}
      <div className="flex items-end justify-between gap-2 h-96 px-4 pt-20">
        {buckets.map((bucket, index) => {
          const value = data[bucket] || 0;
          const percentage = totalChurned > 0 ? (value / totalChurned) * 100 : 0;
          const barHeight = maxValue > 0 ? (value / maxValue) * 100 : 0;

          return (
            <motion.div
              key={bucket}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * index }}
              className="flex-1 flex flex-col items-center group relative"
              onMouseEnter={() => setHoveredBucket(bucket)}
              onMouseLeave={() => setHoveredBucket(null)}
            >
              {/* Tooltip */}
              {hoveredBucket === bucket && value > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute -top-20 left-1/2 transform -translate-x-1/2 z-50 pointer-events-none"
                >
                  <div className="bg-gray-900 dark:bg-gray-700 text-white px-4 py-3 rounded-lg shadow-xl border border-gray-700 dark:border-gray-600 min-w-[180px]">
                    <div className="text-xs font-semibold text-gray-300 mb-1">{bucket}</div>
                    <div className="text-lg font-bold">{value} subscribers</div>
                    <div className="text-xs text-gray-400 mt-1">
                      {percentage.toFixed(1)}% of total churn
                    </div>
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900 dark:bg-gray-700 border-r border-b border-gray-700 dark:border-gray-600"></div>
                  </div>
                </motion.div>
              )}

              {/* Bar Container */}
              <div className="w-full flex flex-col justify-end" style={{ height: '300px' }}>
                {/* Value Display Above Bar */}
                {value > 0 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 + 0.05 * index }}
                    className="text-center mb-2"
                  >
                    <div className="text-sm font-bold text-gray-900 dark:text-white">{value}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {percentage.toFixed(1)}%
                    </div>
                  </motion.div>
                )}

                {/* Bar */}
                <div
                  className="relative w-full bg-gray-100 dark:bg-gray-700 rounded-t-lg overflow-visible cursor-pointer"
                  style={{ height: '100%' }}
                >
                  <motion.div
                    className={`w-full rounded-t-lg transition-all duration-300 absolute bottom-0 ${
                      percentage > 20
                        ? 'bg-gradient-to-t from-red-500 to-red-600'
                        : percentage > 10
                          ? 'bg-gradient-to-t from-orange-500 to-orange-600'
                          : 'bg-gradient-to-t from-blue-500 to-blue-600'
                    }`}
                    initial={{ height: 0 }}
                    animate={{
                      height: value > 0 ? `${barHeight}%` : '0%',
                      scale: hoveredBucket === bucket ? 1.05 : 1,
                    }}
                    transition={{ delay: 0.2 + 0.05 * index, duration: 0.8, ease: 'easeOut' }}
                    style={{
                      boxShadow:
                        hoveredBucket === bucket ? '0 10px 25px -5px rgba(0, 0, 0, 0.3)' : 'none',
                    }}
                  />
                </div>
              </div>

              {/* Enhanced Label */}
              <div className="mt-3 flex flex-col items-center">
                <div
                  className={`text-xs font-semibold transition-all duration-200 ${
                    hoveredBucket === bucket
                      ? 'text-gray-900 dark:text-white scale-110'
                      : 'text-gray-600 dark:text-gray-400'
                  }`}
                >
                  {bucket.replace(' month', '')}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">months</div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap gap-4 justify-center text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-gradient-to-t from-red-500 to-red-600"></div>
            <span className="text-gray-600 dark:text-gray-400">High Churn (&gt;20%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-gradient-to-t from-orange-500 to-orange-600"></div>
            <span className="text-gray-600 dark:text-gray-400">Moderate Churn (10-20%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-gradient-to-t from-blue-500 to-blue-600"></div>
            <span className="text-gray-600 dark:text-gray-400">Low Churn (&lt;10%)</span>
          </div>
        </div>
      </div>

      {/* Insights */}
      {totalChurned > 0 && (
        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <p className="text-sm text-blue-900 dark:text-blue-100">
            <strong>Insight:</strong> Most customers churned at{' '}
            {buckets.reduce((max, bucket) =>
              (data[bucket] || 0) > (data[max] || 0) ? bucket : max
            )}{' '}
            tenure mark
          </p>
        </div>
      )}
    </motion.div>
  );
};

export default ChurnByTenureChart;
